import { Question, Section } from '../types/domain';

export const sections: Section[] = [
  { id: 'reasoning', title: 'Reasoning and Quantitative Aptitude', durationMinutes: 25 },
  { id: 'systems', title: 'Systems and Security', durationMinutes: 25 },
  { id: 'analysis', title: 'Applied Case Studies', durationMinutes: 20 },
  { id: 'writing', title: 'Long-form Response', durationMinutes: 20 }
];

export const questions: Question[] = [
  {
    id: 'q1',
    sectionId: 'reasoning',
    type: 'mcq',
    prompt: 'A team completes 35% of a migration in 14 days. At the same rate, how many days are needed for the full migration?',
    options: ['32 days', '40 days', '44 days', '48 days'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: '40 days'
  },
  {
    id: 'q2',
    sectionId: 'reasoning',
    type: 'mcq',
    prompt: 'Which value completes the sequence: 4, 9, 19, 39, 79, ?',
    options: ['119', '139', '159', '169'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: '159'
  },
  {
    id: 'q3',
    sectionId: 'reasoning',
    type: 'mcq',
    prompt: 'A dashboard refreshes every 1.8 seconds. Approximately how many refreshes occur in 9 minutes?',
    options: ['180', '240', '300', '360'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: '300'
  },
  {
    id: 'q4',
    sectionId: 'reasoning',
    type: 'mcq',
    prompt: 'If a risk score is 40% camera, 30% audio, and 30% browser, what is the score for 70, 30, and 20?',
    options: ['37', '43', '49', '56'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: '43'
  },
  {
    id: 'q5',
    sectionId: 'reasoning',
    type: 'mcq',
    prompt: 'A candidate answers 18 of 20 questions. What percentage remains unanswered?',
    options: ['5%', '8%', '10%', '12%'],
    marks: 1,
    negativeMarks: 0.25,
    expectedAnswer: '10%'
  },
  {
    id: 'q6',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which browser event most directly indicates that a candidate changed tabs or the page became hidden?',
    options: ['visibilitychange', 'mousemove', 'online', 'beforeprint'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'visibilitychange'
  },
  {
    id: 'q7',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Why should webcam inference be throttled in a browser exam application?',
    options: ['To reduce main-thread pressure', 'To increase screen brightness', 'To bypass permissions', 'To force fullscreen'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'To reduce main-thread pressure'
  },
  {
    id: 'q8',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'What is the safest way to render stored rich-text answers from candidates?',
    options: ['Sanitize allowed HTML before persistence/display', 'Trust all pasted HTML', 'Run scripts in a sandboxed iframe', 'Store only screenshots'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'Sanitize allowed HTML before persistence/display'
  },
  {
    id: 'q9',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which Web API can request fullscreen after a candidate clicks a start button?',
    options: ['document.documentElement.requestFullscreen()', 'navigator.sendBeacon()', 'window.print()', 'localStorage.clear()'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'document.documentElement.requestFullscreen()'
  },
  {
    id: 'q10',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Why should localhost not be used in a QR code scanned by a phone?',
    options: ['It resolves to the phone itself', 'It disables CSS', 'It blocks all JavaScript', 'It always uses HTTPS'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'It resolves to the phone itself'
  },
  {
    id: 'q11',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which signal is suitable for administrator review but should not be shown on the candidate exam screen?',
    options: ['Risk score', 'Question number', 'Remaining time', 'Camera connection status'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'Risk score'
  },
  {
    id: 'q12',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which practice helps prevent incident spam from AI detection loops?',
    options: ['Log only on state changes or cooldown boundaries', 'Log every animation frame', 'Disable cleanup handlers', 'Render all logs to the candidate'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'Log only on state changes or cooldown boundaries'
  },
  {
    id: 'q13',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which storage choice avoids native module rebuild failures on Node 22 in this project?',
    options: ['node:sqlite DatabaseSync', 'better-sqlite3 native addon', 'A browser cookie only', 'A CSV in public assets'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'node:sqlite DatabaseSync'
  },
  {
    id: 'q14',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'What should happen when visual AI model loading fails?',
    options: ['Show a clear error and keep the exam usable', 'Freeze the page', 'Auto-submit immediately', 'Hide all camera previews'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'Show a clear error and keep the exam usable'
  },
  {
    id: 'q15',
    sectionId: 'systems',
    type: 'mcq',
    prompt: 'Which statement about browser-based lockdown is accurate?',
    options: ['It is best-effort and cannot guarantee OS-level control', 'It can fully control all operating-system apps', 'It can disable every external device', 'It can block screenshots globally'],
    marks: 2,
    negativeMarks: 0.5,
    expectedAnswer: 'It is best-effort and cannot guarantee OS-level control'
  },
  {
    id: 'q16',
    sectionId: 'analysis',
    type: 'case_study',
    prompt: 'Case Study: Dual-camera proctoring rollout',
    passage:
      'A university is piloting a remote exam platform for 800 candidates. Many candidates use older laptops, and 30% join from mobile hotspots. The product team wants laptop webcam preview, optional phone camera pairing, browser-event logging, and administrator review dashboards. Candidate-facing screens must avoid showing risk labels that could distract or alarm test takers.',
    prompts: [
      'Identify two reliability risks in this rollout.',
      'Recommend two candidate-facing design choices that reduce confusion.',
      'Explain which data should be visible only to reviewers.'
    ],
    marks: 10,
    negativeMarks: 0
  },
  {
    id: 'q17',
    sectionId: 'analysis',
    type: 'case_study',
    prompt: 'Case Study: Warning escalation policy',
    passage:
      'During a mock test, one tab switch triggers blur, visibilitychange, and focus-return events within two seconds. The policy allows three candidate-facing warnings before manual review. The institution does not want automatic submission unless an administrator enables it.',
    prompts: [
      'Describe how event deduplication should work.',
      'State why automatic submission should be configurable.',
      'Define what evidence should still be recorded internally.'
    ],
    marks: 10,
    negativeMarks: 0
  },
  {
    id: 'q18',
    sectionId: 'writing',
    type: 'long_answer',
    prompt: 'Long Answer 1: Explain the trade-offs between candidate privacy and automated proctoring analytics.',
    marks: 12,
    negativeMarks: 0,
    requiredLongAnswer: true
  },
  {
    id: 'q19',
    sectionId: 'writing',
    type: 'long_answer',
    prompt: 'Long Answer 2: Propose a performance strategy for running camera preview, phone pairing, and AI analysis in one browser session.',
    marks: 12,
    negativeMarks: 0,
    requiredLongAnswer: true
  },
  {
    id: 'q20',
    sectionId: 'writing',
    type: 'long_answer',
    prompt: 'Long Answer 3: Describe a reviewer workflow for interpreting warnings, incident evidence, and final recommendations.',
    marks: 12,
    negativeMarks: 0,
    requiredLongAnswer: true
  }
];

export const totalDurationMinutes = sections.reduce((sum, section) => sum + section.durationMinutes, 0);
